/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import * as dcmjs from 'dcmjs';
import { mat3, mat4, ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import { formatDate } from '~/utils/date';
import { DcmJsDatasetWrapper } from '../dicom/dcmJsWrapper';
import { DicomBase } from '../dicom/dicomBase';
import {
    DicomDvh,
    Dose,
    strToDoseSummationType,
    strToDoseType,
    strToDoseUnit,
    strToDvhType,
    strToVolumeUnits,
} from '../dicom/dicomDose';
import { DicomPatient } from '../dicom/dicomPatient';
import { DicomPlan } from '../dicom/dicomPlan';
import { DicomRegistration } from '../dicom/dicomRegistration';
import { DicomRigidRegistration } from '../dicom/dicomRigidRegistration';
import {
    DicomContour,
    DicomStructureSet,
    Roi,
} from '../dicom/dicomstructureset';
import { ImageSlice } from '../dicom/imageSlice';
import { StorageSOPClassUID, strToModality } from '../dicom/modality';
import { PatientDataSet } from '../dicom/patientdataset';
import { DicomSeries, Series } from '../dicom/series';
import { getRandomRoiColor, roundToDecimals } from '../dicom/utils';
import { DoseMatrix } from '../patient/doseMatrix';
import { Image3D } from '../patient/image3d';
import { Patient } from '../patient/patient';
import { Plan } from '../patient/plan';
import { RigidRegistration } from '../patient/rigidRegistration';
import { StructureSet } from '../patient/structureset';
import { PatientRepository } from '../repository/patientRepository';

/** Class for processing DICOM files */

// encapsulation of imported objects
export type ImportResult = {
    patients: PatientDataSet[];
    images: Image3D[];
    doses: DoseMatrix[];
    structureSets: StructureSet[];
    registrations: RigidRegistration[];
};

export class DicomProcessor {
    private processedSeriesUIDs: Set<string> = new Set();
    public static SupportedImagePixelTransferSyntaxes = [
        '1.2.840.10008.1.2', // Implicit VR Little Endian
        '1.2.840.10008.1.2.1', // Explicit VR Little Endian
    ];

    public processDicomData(arrayBuffer: ArrayBuffer): DicomBase | null {
        const dataset = DicomProcessor.readDicomDataset(arrayBuffer);

        switch (dataset.Modality) {
            case 'CT':
            case 'MR':
            case 'PT':
                return this.dicomToImageSlice(dataset);
            case 'RTSTRUCT':
                return this.dicomToStructureSet(dataset);
            case 'RTDOSE':
                return this.dicomToDose(dataset);
            case 'REG':
                return this.dicomToRegistration(dataset);
            case 'RTPLAN':
                return this.dicomToPlan(dataset);
            default:
                console.error(
                    `Attempt to process DICOM data of unsupported modality ${dataset.Modality}.`
                );
                break;
        }

        return null;
    }

    public dicomToImageSlice(dataset: any): ImageSlice | null {
        const imageDataset = this.readImageDataset(dataset);
        if (imageDataset != null) {
            return this.storeImageSlice(imageDataset);
        }
        return null;
    }

    private markAsProcessed(seriesUID: string): boolean {
        if (this.processedSeriesUIDs.has(seriesUID)) {
            return false; // Already processed
        }
        this.processedSeriesUIDs.add(seriesUID);
        return true; // Marked as processed
    }

    private addNewImage(
        series: Series,
        patient: Patient,
        newImages: Image3D[]
    ) {
        const existingImage = patient.getImageWithDicomUid(
            series.seriesInstanceUID
        );
        if (!existingImage && this.markAsProcessed(series.seriesInstanceUID)) {
            const image = Image3D.fromSeries(series);
            if (image) {
                patient.addImage(image);
                newImages.push(image);
            } else {
                console.warn(
                    `Failed to generate a 3D image from series ${series.seriesInstanceUID}`
                );
            }
        }
    }

    private addNewDose(
        series: Series,
        patient: Patient,
        newDoses: DoseMatrix[]
    ) {
        const existingDose = patient.getDoseWithDicomUid(
            series.seriesInstanceUID
        );
        if (!existingDose && this.markAsProcessed(series.seriesInstanceUID)) {
            const dose = DoseMatrix.fromDose(series.dicomDoses[0]);
            patient.addDose(dose);
            newDoses.push(dose);
        }
    }

    private addNewStructureSet(
        series: Series,
        patient: Patient,
        newStructureSets: StructureSet[]
    ) {
        for (const ss of series.dicomStructureSets) {
            const existingStructureSet =
                patient.getStructureSetWithSopInstanceUid(ss.sopInstanceUID);
            if (
                !existingStructureSet &&
                this.markAsProcessed(series.seriesInstanceUID)
            ) {
                const structureSet = new StructureSet(
                    ss,
                    ss.frameOfReferenceUid,
                    null
                );
                patient.addStructureSet(structureSet);
                newStructureSets.push(structureSet);
            }
        }
    }

    private addNewPlan(series: Series, patient: Patient) {
        for (const plan of series.dicomPlans) {
            if (this.markAsProcessed(series.seriesInstanceUID))
                patient.addPlan(
                    new Plan(
                        plan,
                        plan.sopInstanceUID,
                        plan.rtPlanLabel,
                        plan.referencedRtStructureSetUid
                    )
                );
        }
    }

    private addNewRegistration(
        series: Series,
        patient: Patient,
        newRegistrations: RigidRegistration[]
    ) {
        for (const reg of series.dicomRegistrations) {
            if (
                !patient.registrations.find(
                    (r) => r.dicomParent.sopInstanceUID === reg.sopInstanceUID
                )
            ) {
                const rigidRegistration = new RigidRegistration(
                    reg.sourceFrameOfReferenceUid,
                    reg.targetFrameOfReferenceUid,
                    reg.contentLabel,
                    reg.transformation,
                    reg
                );
                patient.addRegistration(rigidRegistration);
                newRegistrations.push(rigidRegistration);
            }
        }
    }

    public toRepository(patientRepository: PatientRepository): ImportResult {
        const newPatients = new Array<PatientDataSet>();
        const newImages = new Array<Image3D>();
        const newDoses = new Array<DoseMatrix>();
        const newStructureSets = new Array<StructureSet>();
        const newRegistrations = new Array<RigidRegistration>();

        for (const patient of this.patients) {
            let existingPatient = patientRepository.getByPatientId(
                patient.PatientID
            );
            if (!existingPatient) {
                existingPatient = new Patient(patient);
                patientRepository.add(existingPatient);
                newPatients.push(patient);
            }

            for (const series of patient.series) {
                if (series.is3DImage) {
                    this.addNewImage(series, existingPatient, newImages);
                } else if (series.isDoseSeries) {
                    this.addNewDose(series, existingPatient, newDoses);
                } else if (series.isStructuresetSeries) {
                    this.addNewStructureSet(
                        series,
                        existingPatient,
                        newStructureSets
                    );
                } else if (series.isPlanSeries) {
                    this.addNewPlan(series, existingPatient);
                } else if (series.isRegistrationSeries) {
                    this.addNewRegistration(
                        series,
                        existingPatient,
                        newRegistrations
                    );
                }
            }

            this.connectObjects(existingPatient);
        }
        return {
            patients: newPatients,
            images: newImages,
            doses: newDoses,
            structureSets: newStructureSets,
            registrations: newRegistrations,
        };
    }

    private connectObjects(patient: Patient) {
        for (const ss of patient.structureSets) {
            if (!ss.image) {
                findImage(ss);
            }
        }

        function findImage(ss: StructureSet) {
            for (const image of patient.images) {
                for (const refSliceUid of ss.dicomStructureSet
                    .referencedSopUids) {
                    if (image.hasSlice(refSliceUid)) {
                        ss.image = image;
                        return;
                    }
                }
            }
        }

        for (const dose of patient.doses) {
            if (!dose.plan) {
                for (const plan of patient.plans) {
                    if (dose.referencesPlanUid(plan.instanceUid)) {
                        dose.plan = plan;
                        plan.doseMatrix = dose;
                        break;
                    }
                }
                if (!dose.plan && dose.dicomDose?.referencedRtStructureSetUid) {
                    for (const ss of patient.structureSets) {
                        if (
                            dose.dicomDose?.referencedRtStructureSetUid ===
                            ss.dicomStructureSet.sopInstanceUID
                        ) {
                            dose.structureSet = ss;
                        }
                    }
                }
            }
        }

        for (const plan of patient.plans) {
            if (!plan.structureSet) {
                for (const ss of patient.structureSets) {
                    if (
                        plan.referencedStructureSetUid ===
                        ss.dicomStructureSet.sopInstanceUID
                    ) {
                        plan.structureSet = ss;
                    }
                }
            }
        }
    }

    private readImageDataset(imageDataset: any): any {
        if (
            !imageDataset.PixelSpacing ||
            !imageDataset.ImagePositionPatient ||
            !imageDataset.ImageOrientationPatient
        ) {
            throw new Error(
                'Missing required fields in DICOM dataset',
                imageDataset
            );
        }
        if (
            !DicomProcessor.SupportedImagePixelTransferSyntaxes.some(
                (x) => x === imageDataset._meta.TransferSyntaxUID.Value[0]
            )
        ) {
            throw new Error(
                `Unsupported transfer syntax '${imageDataset._meta.TransferSyntaxUID.Value[0]}'. Compressed image data is not supported.`,
                imageDataset
            );
        }
        return imageDataset;
    }

    public static readDicomDataset(arrayBuffer: ArrayBuffer): any {
        const dicomData = dcmjs.data.DicomMessage.readFile(
            ensureValidDicom(arrayBuffer)
        );
        const dicomDataset = dcmjs.data.DicomMetaDictionary.naturalizeDataset(
            dicomData.dict
        );
        dicomDataset._meta = dcmjs.data.DicomMetaDictionary.namifyDataset(
            dicomData.meta
        );
        return dicomDataset;
    }

    public static cloneDicomDataset(dicomBase: DicomBase): any {
        const datasetBuf = dcmjs.data
            .datasetToDict(dicomBase.datasetWrapper.dataset)
            .write();
        const datasetClone = DicomProcessor.readDicomDataset(datasetBuf);
        return datasetClone;
    }

    public dicomToStructureSet(dicomDataset: any): DicomStructureSet | null {
        if (dicomDataset != null) {
            return this.storeStructureSet(dicomDataset);
        }
        return null;
    }

    public dicomToDose(dataset: any): Dose | null {
        if (
            !dataset.PixelSpacing ||
            !dataset.ImagePositionPatient ||
            !dataset.ImageOrientationPatient ||
            !dataset.DoseType ||
            !dataset.DoseUnits
        ) {
            return null; // This dicom file is not a dose
        }
        return this.storeDose(dataset);
    }

    public dicomToRegistration(dataset: any): DicomRegistration | null {
        return dataset !== null ? this.storeRegistration(dataset) : null;
    }

    public dicomToPlan(dataset: any): DicomPlan | null {
        return dataset !== null ? this.storePlan(dataset) : null;
    }

    private storeImageSlice(imageDataset: any): ImageSlice | null {
        const imageSlice = dcmjsToImageSlice(imageDataset);

        if (imageSlice === null) {
            console.error(
                `Attempt to load ImageSlice ${imageDataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(imageDataset, imageSlice);
        return imageSlice;
    }

    private storeStructureSet(dataset: any): DicomStructureSet | null {
        const structureSet = dcmjsToStructureSet(dataset);

        if (structureSet === null) {
            console.error(
                `Attempt to load StructureSet ${dataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(dataset, structureSet);
        return structureSet;
    }

    private storeDose(dataset: any): Dose | null {
        const dose = dcmjsToDose(dataset);

        if (dose === null) {
            console.error(
                `Attempt to load Dose ${dataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(dataset, dose);
        return dose;
    }

    private storePlan(dataset: any): DicomPlan | null {
        const plan = dcmjsToPlan(dataset);

        if (plan === null) {
            console.error(
                `Attempt to load Plan ${dataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(dataset, plan);
        return plan;
    }

    private storeRegistration(dataset: any): DicomRegistration | null {
        const registration = dcmjsToRegistration(dataset);

        if (registration === null) {
            console.error(
                `Attempt to load Registration ${dataset.SOPInstanceUID} from DICOM datasource failed.`
            );
            return null;
        }
        this.storeSeriesObject(dataset, registration);
        return registration;
    }

    private storeSeriesObject(dataset: any, dicomBase: DicomBase) {
        const patient = this.getOrCreatePatient(
            dataset.PatientID,
            dataset.PatientName[0]?.Alphabetic
        );
        const series = this.getOrCreateSeries(dataset, patient);
        series.addSeriesObject(dicomBase);
    }

    private getOrCreatePatient(
        patientId: string,
        patientName: string
    ): PatientDataSet {
        const patient = this._patients.get(patientId);
        if (!patient) {
            const pds = new PatientDataSet({
                patientName: patientName,
                patientId: patientId,
            } as DicomPatient);
            this._patients.set(patientId, pds);
            return pds;
        }
        return patient;
    }

    private findSeries(
        patient: PatientDataSet,
        seriesInstanceUID: string
    ): Series | undefined {
        return patient.series.find(
            (ser) => ser.seriesInstanceUID === seriesInstanceUID
        );
    }

    private getOrCreateSeries(dataset: any, patient: PatientDataSet): Series {
        const series = this.findSeries(
            patient,
            dataset.SeriesInstanceUID as string
        );
        if (series === undefined) {
            const dicomSeriesModule: DicomSeries = {
                modality: strToModality(dataset.Modality),
                seriesInstanceUID: dataset.SeriesInstanceUID,
                seriesDescription: dataset.SeriesDescription ?? '',
                seriesNumber: dataset.SeriesNumber,
                seriesDate: formatDate(dataset.SeriesDate),
                frameOfReferenceUid: dataset.FrameOfReferenceUID,
            };
            const ser = new Series(dicomSeriesModule);
            patient.series.push(ser);
            return ser;
        }
        return series;
    }

    private readonly _patients = new Map<string, PatientDataSet>();

    public get patients(): PatientDataSet[] {
        return Array.from(this._patients.values());
    }
}

function dcmjsToImageSlice(dataset: any): ImageSlice {
    CheckMandatory(dataset, 'SOPInstanceUID');
    CheckMandatory(dataset, 'SeriesInstanceUID');
    CheckMandatory(dataset, 'FrameOfReferenceUID');

    const directionCosineFirstRow = vec3.fromValues(
        parseFloat(dataset.ImageOrientationPatient[0]),
        parseFloat(dataset.ImageOrientationPatient[1]),
        parseFloat(dataset.ImageOrientationPatient[2])
    );
    const directionCosineFirstCol = vec3.fromValues(
        parseFloat(dataset.ImageOrientationPatient[3]),
        parseFloat(dataset.ImageOrientationPatient[4]),
        parseFloat(dataset.ImageOrientationPatient[5])
    );
    const directionCosineNormal = vec3.create();
    vec3.cross(
        directionCosineNormal,
        directionCosineFirstRow,
        directionCosineFirstCol
    );

    const rescaleIntercept = parseFloat(dataset.RescaleIntercept) || 0.0;
    const rescaleSlope = parseFloat(dataset.RescaleSlope) || 1.0;

    const headFirstProne = dataset.PatientPosition === 'HFP';
    const tilted = !dataset.ImageOrientationPatient.map((value: any) =>
        parseFloat(value)
    ).every(
        (value: number) => value === 0.0 || value === 1.0 || value === -1.0
    );
    const mrSpecificaAttrs = getMRModalitySpecificAttributes(dataset);
    const ctSpecificAttrs = getCTModalitySpecificAttributes(dataset);

    const pixelSpacing = vec2.fromValues(
        parseFloat(dataset.PixelSpacing[0]),
        parseFloat(dataset.PixelSpacing[1])
    );

    // only uniform pixel spacing is supported
    CheckAndThrow(
        Math.abs(pixelSpacing[0] - pixelSpacing[1]) < 1e-2,
        `Non-uniform pixel spacing: ${pixelSpacing[0].toFixed(
            3
        )}, ${pixelSpacing[1].toFixed(3)}`,
        'PixelSpacing'
    );

    const imageSlice = new ImageSlice(
        dataset.SOPInstanceUID,
        dataset.Modality,
        pixelSpacing,
        vec3.fromValues(
            parseFloat(dataset.ImagePositionPatient[0]),
            parseFloat(dataset.ImagePositionPatient[1]),
            parseFloat(dataset.ImagePositionPatient[2])
        ),
        mat3.fromValues(
            directionCosineFirstRow[0],
            directionCosineFirstRow[1],
            directionCosineFirstRow[2],
            directionCosineFirstCol[0],
            directionCosineFirstCol[1],
            directionCosineFirstCol[2],
            directionCosineNormal[0],
            directionCosineNormal[1],
            directionCosineNormal[2]
        ),
        parseFloat(dataset.SliceThickness),
        dataset.SeriesDescription ?? '',
        dataset.SeriesInstanceUID,
        dataset.FrameOfReferenceUID,
        dataset.Manufacturer ?? '',
        dataset.ManufacturerModelName ?? '',
        dataset.InstanceNumber,
        dataset.ImageType ?? ['UNKNOWN', 'UNKNOWN'],
        dataset.Rows,
        dataset.Columns,
        dataset.PatientPosition ?? '',
        dataset.StationName ?? '',
        dataset.FilterType ?? '',
        rescaleIntercept,
        rescaleSlope,
        headFirstProne,
        tilted,
        new DcmJsDatasetWrapper(dataset),
        ctSpecificAttrs.kvp,
        ctSpecificAttrs.ctdi,
        ctSpecificAttrs.kernel,
        ctSpecificAttrs.protocolName,
        mrSpecificaAttrs.scanningSequence,
        mrSpecificaAttrs.mraAcquisitionType,
        mrSpecificaAttrs.sequenceVariant
    );

    return imageSlice;
}

function dcmjsToDose(dataset: any): Dose {
    CheckAndThrow(
        dataset.PixelRepresentation === 0,
        `Unsupported PixelRepresentation '${dataset.PixelRepresentation}'. SOP UID ${dataset.SOPInstanceUID}`,
        'PixelRepresentation'
    );
    CheckAndThrow(
        dataset.BitsAllocated === 16 || dataset.BitsAllocated === 32,
        `Unsupported BitsAllocated ${dataset.BitsAllocated}. SOP UID ${dataset.SOPInstanceUID}`,
        'BitsAllocated'
    );
    CheckAndThrow(
        dataset.HighBit === dataset.BitsStored - 1,
        `Unsupported HighBit '${dataset.HighBit}'. SOP UID ${dataset.SOPInstanceUID}`,
        'HighBit'
    );

    const directionCosineFirstRow = vec3.fromValues(
        parseFloat(dataset.ImageOrientationPatient[0]),
        parseFloat(dataset.ImageOrientationPatient[1]),
        parseFloat(dataset.ImageOrientationPatient[2])
    );
    const directionCosineFirstCol = vec3.fromValues(
        parseFloat(dataset.ImageOrientationPatient[3]),
        parseFloat(dataset.ImageOrientationPatient[4]),
        parseFloat(dataset.ImageOrientationPatient[5])
    );
    const directionCosineNormal = vec3.create();
    vec3.cross(
        directionCosineNormal,
        directionCosineFirstRow,
        directionCosineFirstCol
    );

    const rtplanReference = dataset.ReferencedRTPlanSequence
        ? dataset.ReferencedRTPlanSequence[0].ReferencedSOPInstanceUID
        : '';

    const referencedRtStructureSetUid = dataset.ReferencedStructureSetSequence
        ? dataset.ReferencedStructureSetSequence[0].ReferencedSOPInstanceUID
        : '';

    const sliceResolution = getSliceResolution(dataset.GridFrameOffsetVector);

    const dicomDvhs = importDvhSequence(dataset.DVHSequence);

    const dose = new Dose(
        dataset.SOPInstanceUID,
        dataset.FrameOfReferenceUID,
        vec2.fromValues(
            parseFloat(dataset.PixelSpacing[0]),
            parseFloat(dataset.PixelSpacing[1])
        ),
        sliceResolution,
        vec3.fromValues(
            parseFloat(dataset.ImagePositionPatient[0]),
            parseFloat(dataset.ImagePositionPatient[1]),
            parseFloat(dataset.ImagePositionPatient[2])
        ),
        mat3.fromValues(
            directionCosineFirstRow[0],
            directionCosineFirstRow[1],
            directionCosineFirstRow[2],
            directionCosineFirstCol[0],
            directionCosineFirstCol[1],
            directionCosineFirstCol[2],
            directionCosineNormal[0],
            directionCosineNormal[1],
            directionCosineNormal[2]
        ),
        dataset.SeriesDescription,
        dataset.Manufacturer,
        dataset.ManufacturerModelName,
        dataset.Rows,
        dataset.Columns,
        dataset.NumberOfFrames,
        strToDoseUnit(dataset.DoseUnits),
        strToDoseType(dataset.DoseType),
        strToDoseSummationType(dataset.DoseSummationType),
        dataset.DoseComment,
        dataset.DoseGridScaling,
        dataset.SoftwareVersions,
        rtplanReference,
        referencedRtStructureSetUid,
        dicomDvhs,
        new DcmJsDatasetWrapper(dataset)
    );

    return dose;
}

/**
 *
 * @param dataset
 * TODO: Currently only supports single registration in a single file. Technically there could be also more than that.
 */
function dcmjsToRegistration(dataset: any): DicomRegistration {
    if (dataset.DeformableRegistrationSequence) {
        throw new Error(
            `Attempt to read DeformableRegistration from dcmjs dataset. Deformable registrations are currently not supported.`
        );
    } else if (dataset.RegistrationSequence) {
        CheckAndThrow(
            dataset.RegistrationSequence.length > 0,
            `Unsupported empty RegistrationSequence. SOP UID ${dataset.SOPInstanceUID}`,
            `RegistrationSequence`
        );
        const sourceFrameOfReferenceUid: string = dataset.FrameOfReferenceUID;
        for (let i = 0; i < dataset.RegistrationSequence.length; ++i) {
            const matrixSequence =
                dataset.RegistrationSequence[i].MatrixRegistrationSequence
                    .MatrixSequence;
            const array = matrixSequence.FrameOfReferenceTransformationMatrix;
            CheckAndThrow(
                array,
                `Rigid registration MatrixSequence missing FrameOfReferenceTransformationMatrix. SOP UID ${dataset.SOPInstanceUID}`,
                `FrameOfReferenceTransformationMatrix`
            );
            CheckAndThrow(
                array.length === 16,
                `Rigid registration FrameOfReferenceTransformationMatrix has a matrix of length ${array.length}. SOP UID ${dataset.SOPInstanceUID}`,
                `FrameOfReferenceTransformationMatrix`
            );
            const transformationMatrix = mat4.fromValues(
                array[0],
                array[4],
                array[8],
                array[12],
                array[1],
                array[5],
                array[9],
                array[13],
                array[2],
                array[6],
                array[10],
                array[14],
                array[3],
                array[7],
                array[11],
                array[15]
            );
            if (!mat4.equals(transformationMatrix, mat4.create())) {
                return new DicomRigidRegistration(
                    dataset.SOPInstanceUID,
                    sourceFrameOfReferenceUid,
                    dataset.RegistrationSequence[i].FrameOfReferenceUID,
                    dataset.ContentLabel,
                    transformationMatrix,
                    new DcmJsDatasetWrapper(dataset)
                );
            }
        }
        throw new Error(
            `Attempt to decode registration, but all the registrations were identity`
        );
    } else {
        throw new Error(
            `Attempt to decode registration that does not contain RegistrationSequence.`
        );
    }
}

/**
 * Convert raw DICOM 16-bit image slice data to an array
 */
export function dicomSlicePixelDataToInternal(
    pixelRepresentation: number,
    arraybuf: ArrayBuffer,
    bitsAllocated: number,
    bitsStored: number,
    slope: number,
    intercept: number
): Int16Array {
    if (slope) {
        let mask = bitsStored && -1 >>> (32 - bitsStored);
        if (pixelRepresentation == 1 && bitsStored < 16) {
            // Hitachi CT images had an example where the pixel data was 13 bits stored in 16 bits with high bit still being the bit 16
            mask = mask << (16 - bitsStored);
        }
        const pixelFn = (val: number) => {
            return (val & mask) * slope + intercept;
        };
        return Int16Array.from(
            bufferToArray(arraybuf, pixelRepresentation, bitsAllocated),
            pixelFn
        );
    } else {
        const array = bufferToArray(
            arraybuf,
            pixelRepresentation,
            bitsAllocated
        );
        return array instanceof Int16Array ? array : new Int16Array(array);
    }
}

/**
 * Convert raw DICOM 32-bit dose matrix data to an array
 */
export function dicomDosePixelDataToInternal(
    pixelRepresentation: number,
    arraybuf: ArrayBuffer,
    bitsAllocated: number,
    bitsStored: number
): Uint32Array {
    const mask = -1 >>> (32 - bitsStored);
    const pixelFn = (val: number) => {
        return val & mask;
    };
    return Uint32Array.from(
        bufferToArray(arraybuf, pixelRepresentation, bitsAllocated),
        pixelFn
    );
}

function bufferToArray(
    arraybuf: ArrayBuffer,
    pixelRepresentation: number,
    bitsAllocated: number
): Uint16Array | Int16Array | Uint32Array {
    if (bitsAllocated === 16) {
        if (pixelRepresentation === 0) {
            return new Uint16Array(arraybuf);
        } else {
            return new Int16Array(arraybuf);
        }
    }

    if (bitsAllocated === 32) {
        if (pixelRepresentation !== 0) {
            throw new Error(
                `Unexpected pixelRepresentation ${pixelRepresentation} with ${bitsAllocated}-bit data`
            );
        }
        return new Uint32Array(arraybuf);
    }

    throw new Error(
        `Could not initialize byte buffer, pixelRepresentation ${pixelRepresentation}, bitsAllocated ${bitsAllocated}`
    );
}

export function dcmjsToStructureSet(dataset: any): DicomStructureSet | null {
    if (
        !dataset.ReferencedFrameOfReferenceSequence ||
        !dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence ||
        !dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence
            .RTReferencedSeriesSequence ||
        !dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence
            .RTReferencedSeriesSequence.SeriesInstanceUID
    ) {
        return null;
    }

    const refSeriesInstanceUID =
        dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence.RTReferencedSeriesSequence.SeriesInstanceUID.substring(
            0,
            64
        );
    const refSeriesSOPClassUID =
        dataset.ReferencedFrameOfReferenceSequence.RTReferencedStudySequence
            .RTReferencedSeriesSequence?.ContourImageSequence[0]
            ?.ReferencedSOPClassUID ?? StorageSOPClassUID.CTImageStorage;

    const frameOfReferenceUid =
        dataset.ReferencedFrameOfReferenceSequence.FrameOfReferenceUID;
    const sopInstanceUID = dataset.SOPInstanceUID;

    const datasetWrapper = new DcmJsDatasetWrapper(dataset);
    const referencedSopUids = new Array<string>();
    const rois = parseRois(datasetWrapper, referencedSopUids);

    const ss = new DicomStructureSet(
        datasetWrapper,
        sopInstanceUID,
        frameOfReferenceUid,
        refSeriesInstanceUID,
        dataset.StructureSetLabel,
        dataset.StructureSetDescription,
        rois,
        referencedSopUids,
        refSeriesSOPClassUID,
        dataset.SeriesInstanceUID
    );

    return ss;
}

function parseRois(
    datasetWrapper: DcmJsDatasetWrapper,
    referencedSopUids: Array<string>
): Map<number, Roi> {
    const dataset = datasetWrapper.dataset as any;
    const rois = new Map<number, Roi>();
    const structureSetROISequence = Array.isArray(
        dataset.StructureSetROISequence
    )
        ? dataset.StructureSetROISequence
        : [dataset.StructureSetROISequence];
    structureSetROISequence.forEach(
        (roi: { ROINumber: number; ROIName: string }) => {
            const roiNumber = roi.ROINumber;
            const name = roi.ROIName;
            rois.set(roiNumber, new Roi(datasetWrapper, roiNumber, name));
        }
    );
    const roiObservationsSequence = Array.isArray(
        dataset.RTROIObservationsSequence
    )
        ? dataset.RTROIObservationsSequence
        : [dataset.RTROIObservationsSequence];
    roiObservationsSequence.forEach(
        (refRoi: {
            ReferencedROINumber: number;
            RTROIInterpretedType: string;
        }) => {
            if (refRoi) {
                const roi = rois.get(refRoi.ReferencedROINumber);
                if (!roi) {
                    console.warn(
                        `StructureSet ROIObservationSequence contains roiNumber ${refRoi.ReferencedROINumber}, but that ROINumber is not included in the structureSetROISequence`
                    );
                }
            } else {
                console.warn(
                    `StructureSet ROIObservationSequence contains empty value`
                );
            }
        }
    );

    dataset.ROIContourSequence = Array.isArray(dataset.ROIContourSequence)
        ? dataset.ROIContourSequence
        : [dataset.ROIContourSequence];
    dataset.ROIContourSequence.forEach((roiContour: any) => {
        const roi = rois.get(roiContour.ReferencedROINumber);
        if (roi) {
            roi.contours = [];
            const color = roiContour.ROIDisplayColor
                ? roiContour.ROIDisplayColor.map((x: any) => parseInt(x))
                : getRandomRoiColor();
            roi.color = { R: color[0], G: color[1], B: color[2] };

            roiContour.ContourSequence = Array.isArray(
                roiContour.ContourSequence
            )
                ? roiContour.ContourSequence
                : [roiContour.ContourSequence];

            // Workaround for dmjs bug: if ContourSequence contains undefined values only, serialization back to DICOM will fail later
            if (
                roiContour.ContourSequence.length > 0 &&
                roiContour.ContourSequence.every(
                    (x: unknown) => x === undefined
                )
            ) {
                roiContour.ContourSequence = [];
            }

            for (const contour of roiContour.ContourSequence) {
                if (!contour || !contour.ContourImageSequence) continue;

                if (contour.ContourGeometricType !== 'CLOSED_PLANAR') {
                    console.error(
                        `Unsupported contour type ${contour.ContourGeometricType}`
                    );
                    continue;
                }

                const sliceId = (
                    contour.ContourImageSequence
                        .ReferencedSOPInstanceUID as string
                ).trim();

                if (!referencedSopUids.includes(sliceId)) {
                    referencedSopUids.push(sliceId);
                }

                const nrPts = parseInt(contour.NumberOfContourPoints);
                const data = contour.ContourData;

                CheckAndThrow(
                    nrPts * 3 === data.length,
                    `Unexpected number of contour points ${nrPts} while ContourData length is ${data.length}`,
                    'NumberOfContourPoints'
                );

                const points: vec3[] = [];
                for (let i = 0; i < nrPts; ++i) {
                    const x = parseFloat(data[i * 3]);
                    const y = parseFloat(data[i * 3 + 1]);
                    const z = parseFloat(data[i * 3 + 2]);
                    points.push(vec3.fromValues(x, y, z));
                }

                if (points.length > 0) {
                    roi.contours.push(
                        new DicomContour(
                            contour.ContourGeometricType,
                            points,
                            contour.ContourImageSequence[0].ReferencedSOPClassUID,
                            contour.ContourImageSequence[0].ReferencedSOPInstanceUID
                        )
                    );
                }
            }
        } else {
            console.warn();
        }
    });
    return rois;
}

export function getMRModalitySpecificAttributes(dataset: any) {
    const scanningSequence = dataset.ScanningSequence ?? undefined;
    const mraAcquisitionType = dataset.MRAcquisitionType ?? undefined;
    const sequenceVariant = dataset.SequenceVariant ?? undefined;
    return { scanningSequence, mraAcquisitionType, sequenceVariant };
}

export function getCTModalitySpecificAttributes(dataset: any) {
    const kvp = dataset.KVP ? `${dataset.KVP} kV` : undefined;
    const ctdi = dataset.CTDIvol
        ? `${dataset.CTDIvol.toFixed(2)} mGy`
        : undefined;
    const kernel = Array.isArray(dataset.ConvolutionKernel)
        ? dataset.ConvolutionKernel.join(', ')
        : typeof dataset.ConvolutionKernel === 'string'
          ? dataset.ConvolutionKernel
          : undefined;
    const protocolName = dataset.ProtocolName ?? undefined;
    return { kvp, ctdi, kernel, protocolName };
}

export function dcmjsUpdateContourSequence(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    contours: DicomContour[]
) {
    const dataset = datasetWrapper.dataset as any;
    const roiSequenceItem = dataset.ROIContourSequence.find(
        (x: { ReferencedROINumber: number }) =>
            x.ReferencedROINumber === roiNumber
    );
    if (!roiSequenceItem) {
        throw new Error(
            `ROIContourSequence Item with ROINumber ${roiNumber} not found`
        );
    }

    // round the points to 3 decimals. DICOM does not like endless floating points
    const clampPoint = (point: ReadonlyVec3) => {
        return [
            roundToDecimals(point[0], 3),
            roundToDecimals(point[1], 3),
            roundToDecimals(point[2], 3),
        ];
    };
    roiSequenceItem.ContourSequence = contours.map((contour) => {
        return {
            ContourGeometricType: contour.type,
            NumberOfContourPoints: contour.length,
            ContourData: contour.data.map((point) => clampPoint(point)).flat(),
            ContourImageSequence: {
                ReferencedSOPClassUID: contour.referencedSOPClassUID,
                ReferencedSOPInstanceUID: contour.referencedSOPInstanceUID,
            },
        };
    });
}

export function dcmjsRemoveStructure(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number
) {
    const removeItemByROINumber = (sequence: any[], key: string) => {
        if (!sequence) return;
        const index = sequence.findIndex(
            (x: { [key: string]: number }) => x[key] === roiNumber
        );
        if (index !== -1) {
            sequence.splice(index, 1);
        }
    };
    const dataset = datasetWrapper.dataset as any;
    removeItemByROINumber(dataset.StructureSetROISequence, 'ROINumber');
    removeItemByROINumber(
        dataset.RTROIObservationsSequence,
        'ReferencedROINumber'
    );
    removeItemByROINumber(dataset.ROIContourSequence, 'ReferencedROINumber');
}

export function dcmjsReadRoiSequenceProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string
): T | undefined {
    const roi = (datasetWrapper.dataset as any).StructureSetROISequence.find(
        (x: { ROINumber: number }) => x.ROINumber === roiNumber
    );
    return roi ? (roi[propertyName] as T) : undefined;
}

export function dcmjsWriteRoiSequenceProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string,
    value: T
) {
    const roi = (datasetWrapper.dataset as any).StructureSetROISequence.find(
        (x: { ROINumber: number }) => x.ROINumber === roiNumber
    );
    if (!roi) {
        throw new Error(`ROI with ROINumber ${roiNumber} not found`);
    }
    roi[propertyName] = value;
}

export function dcmjsWriteContourSequenceProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string,
    value: T
) {
    const roi = (datasetWrapper.dataset as any).ROIContourSequence.find(
        (x: { ReferencedROINumber: number }) =>
            x.ReferencedROINumber === roiNumber
    );
    if (!roi) {
        throw new Error(`ROI with ROINumber ${roiNumber} not found`);
    }
    roi[propertyName] = value;
}

export function dcmjsReadRoiObservationsProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string
): T | undefined {
    const roi = (datasetWrapper.dataset as any).RTROIObservationsSequence.find(
        (x: { ReferencedROINumber: number }) =>
            x.ReferencedROINumber === roiNumber
    );
    return roi ? (roi[propertyName] as T) : undefined;
}

export function dcmjsWriteRoiObservationsProperty<T>(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyName: string,
    value: T
) {
    const roi = (datasetWrapper.dataset as any).RTROIObservationsSequence.find(
        (x: { ReferencedROINumber: number }) =>
            x.ReferencedROINumber === roiNumber
    );
    if (!roi) {
        throw new Error(`ROI with ROINumber ${roiNumber} not found`);
    }
    roi[propertyName] = value;
}

export function dcmjsWriteRoiPhysicalProperty(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number,
    propertyValue: number | undefined
) {
    let physicalPropertiesSequence: {
        ROIPhysicalProperty: string;
        ROIPhysicalPropertyValue: number;
    }[] = [];
    if (propertyValue !== undefined && !isNaN(propertyValue)) {
        physicalPropertiesSequence = [
            {
                ROIPhysicalProperty: 'REL_ELEC_DENSITY',
                ROIPhysicalPropertyValue: propertyValue,
            },
        ];
    }
    dcmjsWriteRoiObservationsProperty(
        datasetWrapper,
        roiNumber,
        'ROIPhysicalPropertiesSequence',
        physicalPropertiesSequence
    );
}

export function dcmjsReadRoiPhysicalProperty(
    datasetWrapper: DcmJsDatasetWrapper,
    roiNumber: number
): { ROIPhysicalProperty: string; ROIPhysicalPropertyValue: number } | undefined {
    const physicalPropertiesSequence = dcmjsReadRoiObservationsProperty<
        { ROIPhysicalProperty: string; ROIPhysicalPropertyValue: number }[]
    >(datasetWrapper, roiNumber, 'ROIPhysicalPropertiesSequence');
    if (
        physicalPropertiesSequence &&
        physicalPropertiesSequence.length == 1 &&
        physicalPropertiesSequence[0].ROIPhysicalProperty === 'REL_ELEC_DENSITY'
    ) {
        return physicalPropertiesSequence[0];
    }
    return undefined;
}

export function dcmjsGetString(
    datasetWrapper: DcmJsDatasetWrapper,
    tag: string
): string {
    return (datasetWrapper.dataset as any)[tag]
        ? (datasetWrapper.dataset as any)[tag]
        : '';
}

export function dcmjsSetString(
    datasetWrapper: DcmJsDatasetWrapper,
    tag: string,
    value: string
) {
    (datasetWrapper.dataset as any)[tag] = value;
}

function getSliceResolution(gridFrameOffsetVector: Array<number>): number {
    if (Math.abs(gridFrameOffsetVector[0]) > 1e-4) {
        // we only support Grid Frame Offset Vector interpretation 'a.' from DICOM standard
        throw new Error(
            `Unexpected grid frame offset of ${gridFrameOffsetVector[0]} for the first slice. Expected 0.`
        );
    }
    const res = gridFrameOffsetVector[1] - gridFrameOffsetVector[0];
    for (let i = 1; i < gridFrameOffsetVector.length; i++) {
        if (
            Math.abs(
                gridFrameOffsetVector[i] - gridFrameOffsetVector[i - 1] - res
            ) > 0.1
        ) {
            throw new Error(
                `Unexpected grid frame offset of ${Math.abs(
                    gridFrameOffsetVector[i] - gridFrameOffsetVector[i - 1]
                )} between slices ${i - 1} and ${i}. Expected ${res}.`
            );
        }
    }
    return res;
}

function dcmjsToPlan(dataset: any): DicomPlan {
    CheckAndThrow(
        dataset.RTPlanGeometry === 'PATIENT',
        `Unsupported RTPlanGeometry '${dataset.RTPlanGeometry}'. SOP UID ${dataset.SOPInstanceUID}`,
        'RTPlanGeometry'
    );

    const rtStructReference = dataset.ReferencedStructureSetSequence
        ? dataset.ReferencedStructureSetSequence[0].ReferencedSOPInstanceUID
        : '';

    const plan = new DicomPlan(
        dataset.SOPInstanceUID,
        dataset.RTPlanLabel,
        rtStructReference,
        new DcmJsDatasetWrapper(dataset)
    );

    return plan;
}

function CheckMandatory(dataset: any, dicomTag: string) {
    if (dataset[dicomTag] === undefined) {
        throw new Error(
            `Mandatory DICOM tag ${dicomTag} is missing in the dataset ${dataset.Modality}: ${dataset.SOPInstanceUID}.`
        );
    }
}

function CheckAndThrow(
    condition: boolean,
    errorMessage: string,
    dicomTag: string
) {
    if (!condition) {
        throw new Error(`DICOM tag ${dicomTag}: ${errorMessage}`);
    }
}

function importDvhSequence(dvhSequence: any): DicomDvh[] {
    if (!dvhSequence) {
        return [];
    }
    const isSupportedDvh = (dvh: any) => {
        return (
            dvh.DVHReferencedROISequence &&
            dvh.DVHReferencedROISequence.length === 1 &&
            dvh.DVHReferencedROISequence[0].DVHROIContributionType ===
                'INCLUDED' &&
            dvh.DoseUnits === 'GY' &&
            dvh.DoseType !== 'ERROR' &&
            dvh.DVHType === 'CUMULATIVE'
        );
    };
    const supportedDvhs = dvhSequence?.filter(isSupportedDvh);
    const nonSupportedDvhs = dvhSequence?.filter(
        (dvh: any) => !isSupportedDvh(dvh)
    );
    if (nonSupportedDvhs?.length) {
        console.warn(
            `Found ${nonSupportedDvhs.length} DVHs that are not supported.`
        );
    }

    return supportedDvhs?.map((dvh: any) => {
        return new DicomDvh(
            strToDoseUnit(dvh.DoseUnits),
            strToDoseType(dvh.DoseType),
            strToDvhType(dvh.DVHType),
            strToVolumeUnits(dvh.DVHVolumeUnits),
            dvh.DVHNumberOfBins,
            dvh.DVHDoseScaling,
            dvh.DVHData,
            dvh.DVHReferencedROISequence[0].ReferencedROINumber,
            dvh.DVHMinimumDose,
            dvh.DVHMeanDose,
            dvh.DVHMaximumDose
        );
    });
}

/**
 * Checks if an ArrayBuffer is a valid DICOM file and adds File Meta Information if needed.
 * @param arrayBuffer The input ArrayBuffer containing potential DICOM data
 * @returns A valid DICOM ArrayBuffer (either original or newly created)
 */
function ensureValidDicom(arrayBuffer: ArrayBuffer): ArrayBuffer {
    // Check if the file already has a valid DICOM header
    if (isValidDicom(arrayBuffer)) {
        return arrayBuffer;
    }

    // Create a new DICOM array with proper File Meta Information
    return fixMissingMetaheader(arrayBuffer);
}

/**
 * Checks if an ArrayBuffer starts with a valid DICOM header
 */
function isValidDicom(arrayBuffer: ArrayBuffer): boolean {
    if (arrayBuffer.byteLength < 132) {
        return false;
    }

    const headerView = new DataView(arrayBuffer, 128, 4);
    return (
        headerView.getUint8(0) === 0x44 && // 'D'
        headerView.getUint8(1) === 0x49 && // 'I'
        headerView.getUint8(2) === 0x43 && // 'C'
        headerView.getUint8(3) === 0x4d // 'M'
    );
}

function fixMissingMetaheader(arrayBuffer: ArrayBuffer): ArrayBuffer {
    console.warn(
        "NOTICE: Trying to load a DICOM file that doesn't have a metaheader. Inserting a preset header to get it to load."
    );

    const originalFile = new Uint8Array(arrayBuffer);

    // Required header elements:
    // * Empty preamble
    // * "DICM" prefix
    // * (0002,0000) *File Meta Information Group Length*, UL, 4
    // * (0002,0001) *File Meta Information Version*, OB, 2
    // * (0002,0002) *Media Storage SOP Class UID*, UI, N
    // * (0002,0003) *Media Storage SOP Instance UID*, UI, N
    // * (0002,0010) *Transfer Syntax UID*, UI, N
    // * (0002,0012) *Implementation Class UID*, UI, N

    const emptyPreamble = new Uint8Array(128);

    // NOTE: this metaheader is copied from an existing DICOM file. It WON'T match whatever file it is
    // being added to, but is usually enough to get dcmjs to load the file correctly.
    // NOTE 2: this array includes the DICM prefix
    const metaheader = new Uint8Array([
        0x44, 0x49, 0x43, 0x4d, 0x02, 0x00, 0x00, 0x00, 0x55, 0x4c, 0x04, 0x00,
        0xd8, 0x00, 0x00, 0x00, 0x02, 0x00, 0x01, 0x00, 0x4f, 0x42, 0x00, 0x00,
        0x02, 0x00, 0x00, 0x00, 0x00, 0x01, 0x02, 0x00, 0x02, 0x00, 0x55, 0x49,
        0x1a, 0x00, 0x31, 0x2e, 0x32, 0x2e, 0x38, 0x34, 0x30, 0x2e, 0x31, 0x30,
        0x30, 0x30, 0x38, 0x2e, 0x35, 0x2e, 0x31, 0x2e, 0x34, 0x2e, 0x31, 0x2e,
        0x31, 0x2e, 0x32, 0x00, 0x02, 0x00, 0x03, 0x00, 0x55, 0x49, 0x2c, 0x00,
        0x32, 0x2e, 0x32, 0x35, 0x2e, 0x31, 0x30, 0x31, 0x34, 0x38, 0x35, 0x34,
        0x34, 0x36, 0x33, 0x34, 0x35, 0x31, 0x36, 0x30, 0x35, 0x32, 0x31, 0x30,
        0x38, 0x30, 0x30, 0x31, 0x34, 0x30, 0x36, 0x30, 0x31, 0x34, 0x36, 0x30,
        0x34, 0x39, 0x31, 0x34, 0x34, 0x32, 0x34, 0x32, 0x02, 0x00, 0x10, 0x00,
        0x55, 0x49, 0x12, 0x00, 0x31, 0x2e, 0x32, 0x2e, 0x38, 0x34, 0x30, 0x2e,
        0x31, 0x30, 0x30, 0x30, 0x38, 0x2e, 0x31, 0x2e, 0x32, 0x00, 0x02, 0x00,
        0x12, 0x00, 0x55, 0x49, 0x40, 0x00, 0x32, 0x2e, 0x32, 0x35, 0x2e, 0x38,
        0x30, 0x33, 0x30, 0x32, 0x38, 0x31, 0x33, 0x31, 0x33, 0x37, 0x37, 0x38,
        0x36, 0x33, 0x39, 0x38, 0x35, 0x35, 0x34, 0x37, 0x34, 0x32, 0x30, 0x35,
        0x30, 0x39, 0x32, 0x36, 0x37, 0x33, 0x34, 0x36, 0x33, 0x30, 0x39, 0x32,
        0x31, 0x36, 0x30, 0x33, 0x33, 0x36, 0x36, 0x36, 0x34, 0x38, 0x32, 0x32,
        0x35, 0x32, 0x31, 0x32, 0x31, 0x34, 0x35, 0x34, 0x30, 0x34, 0x02, 0x00,
        0x13, 0x00, 0x53, 0x48, 0x0a, 0x00, 0x64, 0x63, 0x6d, 0x6a, 0x73, 0x2d,
        0x30, 0x2e, 0x30, 0x20,
    ]);

    const combined = new Uint8Array([
        ...emptyPreamble,
        ...metaheader,
        ...originalFile,
    ]);

    return combined.buffer;
}

export const exportedForTesting = {
    getSliceResolution,
};
